import React, { useState } from 'react';
import styled from '@emotion/styled';
import { X } from 'lucide-react';
import { useModal } from 'context/ModalContext';

const HeaderBar = styled.div`
  position: relative;
  z-index: 10;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.offWhiteDark};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2rem;

  @media (min-width: 768px) {
    padding: 2rem 2.4rem;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
`;

const ProductThumbnail = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 12px;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ProductTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.6rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  letter-spacing: -0.025em;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.tertiaryDark};
  cursor: pointer;
  display: inline;
  margin-left: 4px;

  &:hover {
    text-decoration: none;
  }
`;

const CloseButton = styled.button`
  width: 50px;
  height: 40px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.black};
  background: transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.offWhite};

    svg {
      transform: rotate(90deg);
    }
  }
`;

export const ModalHeader = () => {
  const { item, closeModal } = useModal();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const MAX_DESCRIPTION_LENGTH = 130;
  const shouldTruncate =
    item?.description && item.description.length > MAX_DESCRIPTION_LENGTH;
  const displayText =
    shouldTruncate && !isDescriptionExpanded
      ? item.description.slice(0, MAX_DESCRIPTION_LENGTH).trim() + '...'
      : item?.description;

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const handleDescriptionClick = (e) => {
    if (shouldTruncate) {
      handleExpandClick(e);
    }
  };

  if (!item) return null;

  return (
    <HeaderBar>
      <HeaderContent>
        <HeaderInfo>
          {item.imageUrl && (
            <ProductThumbnail src={item.imageUrl} alt={item.name} />
          )}
          <TextContainer>
            <ProductTitle>{item.name}</ProductTitle>
            {item.description && (
              <ProductDescription
                $clickable={shouldTruncate}
                onClick={handleDescriptionClick}
              >
                {displayText}
                {shouldTruncate && (
                  <ReadMoreButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandClick(e);
                    }}
                  >
                    {isDescriptionExpanded ? 'Read less' : 'Read more'}
                  </ReadMoreButton>
                )}
              </ProductDescription>
            )}
          </TextContainer>
        </HeaderInfo>
        <CloseButton onClick={closeModal} aria-label="Close modal">
          <X size={20} />
        </CloseButton>
      </HeaderContent>
    </HeaderBar>
  );
};

export default ModalHeader;
